import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Search, RefreshCw, LogIn, Plus, FileUp, Edit2, Pin, Trash2, Apple } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Logo } from "./Logo";

export function AppHeader({ searchInputRef, searchQuery, setSearchQuery, handleSearch, searchLoading, searchResults, handleAnalyzeSingle, currentPortfolioId, handleSwitchPortfolio, portfoliosList, handleAddNewPortfolio, loading, handleFileUpload, user, isExample, handleRenamePortfolio, handleSetDefaultPortfolio, defaultPortfolioId, handleDeletePortfolio, handleLogin, handleLogout }: any) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {/* Header Section */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-zinc-800 pb-4 sm:pb-6">
              <div className="flex flex-col gap-4">
                <div>
                  <div className="flex items-center gap-4">
                    <Logo
                      className="h-12 sm:h-16 md:h-20 w-auto transition-transform hover:scale-[1.02]"
                      ariaLabel={t("header.title")}
                    />
                    <button
                      onClick={() => {
                        searchInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setTimeout(() => searchInputRef.current?.focus(), 500);
                      }}
                      className="p-2 hover:bg-zinc-800 rounded-sm transition-colors text-zinc-300"
                      title={t("header.search_placeholder")}
                      aria-label={t("header.search_placeholder")}
                    >
                      <Search size={20} />
                    </button>
                  </div>
                  <h2 className="text-[10px] label-secondary uppercase tracking-[0.2em] mt-2 font-mono ml-2.5 sm:ml-4">
                    {t("header.subtitle")}
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-x-4 gap-y-3 bg-zinc-900/50 p-2 sm:p-2 border border-zinc-800 rounded-sm w-full sm:w-fit">
                  <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3 min-w-0">
                    <div className="flex flex-col flex-1 min-w-0">
                      <label htmlFor="portfolio-select-dropdown" className="text-[9px] uppercase tracking-widest label-secondary">
                        {t("common.portfolio")}
                      </label>
                      <select
                        id="portfolio-select-dropdown"
                        name="portfolio-select"
                        aria-label={t("common.portfolio")}
                        className="bg-transparent text-sm font-bold text-zinc-200 outline-none w-full sm:w-48 truncate"
                        value={currentPortfolioId || ""}
                        onChange={(e) => handleSwitchPortfolio(e.target.value)}
                      >
                        {portfoliosList.map((p) => (
                          <option
                            key={p.id}
                            value={p.id}
                            className="text-zinc-900 bg-white"
                          >
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={handleAddNewPortfolio}
                        className="p-1 hover:bg-zinc-800 rounded-sm transition-colors text-zinc-300 shrink-0"
                        title={t("portfolio.new_portfolio", {
                          defaultValue: "New Portfolio",
                        })}
                        aria-label={t("portfolio.new_portfolio", {
                          defaultValue: "New Portfolio",
                        })}
                      >
                        <Plus size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                            fileInputRef.current.click();
                          }
                        }}
                        disabled={loading}
                        className={`p-1 hover:bg-zinc-800 rounded-sm transition-colors text-zinc-300 shrink-0 ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={t("header.import_menu_label")}
                        aria-label={t("header.import_menu_label")}
                      >
                        {loading ? (
                          <RefreshCw size={16} className="animate-spin text-emerald-500" />
                        ) : (
                          <FileUp size={16} />
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        id="portfolio-file-upload"
                        name="file-upload"
                        type="file"
                        className="hidden"
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        onChange={handleFileUpload}
                        disabled={loading}
                        tabIndex={-1}
                        aria-hidden="true"
                      />
                      {user && portfoliosList.length > 0 && !isExample && (
                        <button
                          onClick={handleRenamePortfolio}
                          className="p-1 hover:bg-zinc-800 rounded-sm transition-colors text-zinc-300 hover:text-indigo-400 shrink-0"
                          title={t("portfolio.rename_portfolio", {
                            defaultValue: "Update Portfolio",
                          })}
                          aria-label={t("portfolio.rename_portfolio", {
                            defaultValue: "Update Portfolio",
                          })}
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      <button
                        onClick={handleSetDefaultPortfolio}
                        disabled={
                          portfoliosList.length <= 1 ||
                          currentPortfolioId === defaultPortfolioId
                        }
                        className={`p-1 rounded-sm transition-colors shrink-0 ${portfoliosList.length <= 1 || currentPortfolioId === defaultPortfolioId ? "text-zinc-300 cursor-not-allowed hidden sm:block opacity-50" : "hover:bg-zinc-800 text-zinc-300 hover:text-amber-400"}`}
                        title={t("portfolio.set_as_default", {
                          defaultValue: "Set as Default",
                        })}
                        aria-label={t("portfolio.set_as_default", {
                          defaultValue: "Set as Default",
                        })}
                      >
                        <Pin size={16} />
                      </button>
                      {user && portfoliosList.length > 0 && !isExample && (
                        <button
                          onClick={handleDeletePortfolio}
                          className="p-1 hover:bg-red-900/30 rounded-sm transition-colors text-zinc-300 hover:text-red-400 shrink-0"
                          title={t("portfolio.delete_portfolio", {
                            defaultValue: "Delete Portfolio",
                          })}
                          aria-label={t("portfolio.delete_portfolio", {
                            defaultValue: "Delete Portfolio",
                          })}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {!user ? (
                    <div className="flex flex-wrap sm:flex-nowrap items-center justify-between sm:justify-center gap-2 sm:gap-3 border-t sm:border-t-0 sm:border-l border-zinc-800 pt-2 sm:pt-0 sm:pl-4">
                      <span className="text-[9px] uppercase tracking-widest label-secondary shrink-0">
                        {t("auth.login_prompt")}
                      </span>
                      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                        <button
                          onClick={() => handleLogin("google")}
                          className="flex items-center justify-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-zinc-200 rounded-sm transition-all hover:text-white group shrink-0"
                          title={t("auth.login_with_google") || "Sign in with Google"}
                        >
                          <LogIn
                            size={14}
                            className="text-emerald-500 group-hover:scale-110 transition-transform"
                          />
                          <span className="text-[10px] font-bold uppercase tracking-tight">
                            Google
                          </span>
                        </button>
                        <button
                          onClick={() => handleLogin("apple")}
                          className="flex items-center justify-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-zinc-200 rounded-sm transition-all hover:text-white group shrink-0"
                          title={t("auth.login_with_apple") || "Sign in with Apple"}
                        >
                          <Apple
                            size={14}
                            className="text-zinc-300 group-hover:text-white group-hover:scale-110 transition-transform"
                          />
                          <span className="text-[10px] font-bold uppercase tracking-tight">
                            Apple
                          </span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex sm:flex-col items-center sm:items-start xl:items-end justify-between border-t sm:border-t-0 sm:border-l border-zinc-800 pt-2 sm:pt-0 sm:pl-4 min-w-0">
                      <span
                        className="text-[10px] label-secondary font-medium truncate w-full sm:max-w-[150px]"
                        title={user.email || "Logged in"}
                      >
                        {user.email || "User"}
                      </span>
                      <button
                        onClick={handleLogout}
                        className="text-[9px] uppercase tracking-widest text-zinc-300 hover:text-zinc-300 shrink-0 mt-1 sm:mt-0"
                      >
                        {t("auth.logout")}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full md:flex-1 max-w-lg md:ml-12">
                <div className="p-3 sm:p-4 bg-zinc-900 border-2 border-zinc-700/50 rounded-sm shadow-xl shadow-emerald-950/5 group hover:border-zinc-500 transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Search size={14} className="text-emerald-500" />
                    <label htmlFor="global-search-input" className="block text-[9px] uppercase tracking-widest font-bold text-zinc-400">
                      {t("header.search_placeholder")}
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      id="global-search-input"
                      ref={searchInputRef}
                      name="global-search"
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      placeholder={t("header.search_placeholder")}
                      aria-label={t("header.search_placeholder")}
                      className="w-full bg-zinc-950 border border-zinc-800 hover:border-zinc-700 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 text-zinc-300 text-sm px-4 py-3 rounded-sm focus:outline-none font-mono transition-all pr-32"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                    <button
                      onClick={handleSearch}
                      disabled={searchLoading || !searchQuery}
                      aria-label={t("common.analyze") || "Analyze stock"}
                      className="absolute right-1 top-1 bottom-1 px-4 bg-zinc-800 border border-zinc-700 text-[10px] text-zinc-100 hover:bg-zinc-700 font-bold uppercase tracking-wider rounded-sm disabled:opacity-30 transition-colors"
                    >
                      {searchLoading ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : (
                        t("header.search_button")
                      )}
                    </button>

                    <AnimatePresence>
                      {searchResults.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-800 shadow-2xl rounded-sm z-50 overflow-x-hidden overflow-y-auto max-h-96 custom-scrollbar"
                        >
                          {searchResults.map((res: any, idx) => (
                            <div
                              key={idx}
                              className="p-3 hover:bg-zinc-800 border-b last:border-0 border-zinc-800/50 flex justify-between items-center cursor-pointer transition-colors"
                              onClick={() => handleAnalyzeSingle(res.symbol, false, true)}
                            >
                              <div>
                                <p className="text-zinc-200 font-bold font-mono">
                                  {res.symbol}{" "}
                                  <span className="text-[9px] text-zinc-300 font-sans tracking-widest">
                                    {res.exchDisp || res.exchange}
                                  </span>
                                </p>
                                <p className="text-[10px] text-zinc-300 truncate max-w-[250px]">
                                  {res.longname || res.shortname || ""}
                                </p>
                              </div>
                              <button className="px-3 py-1 bg-emerald-900/30 text-emerald-500 border border-emerald-500/30 rounded-sm text-[9px] uppercase font-bold tracking-widest hover:bg-emerald-900/50 transition-colors">
                                {t("common.analyze")}
                              </button>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </header>
    </>
  );
}
